import React, { useEffect, useMemo, useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { Trophy, Timer, X, CheckCircle2, Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const FILES = 'abcdefgh';
const EMPTY_FEN = '8/8/8/8/8/8/8/8 w - - 0 1';

// Build a FEN with ONLY the student's chess piece. Targets/obstacles are drawn
// as emoji overlays via customSquareStyles so the read-only mini board mirrors
// exactly what the student sees in CaptureActivityPlayer.
function piecesToFen(pieces) {
  const map = {};
  (pieces || []).forEach(p => { if (p.piece && p.color) map[p.square] = p; });
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const sq = FILES[f] + r;
      const cell = map[sq];
      if (cell) {
        if (empty > 0) { row += empty; empty = 0; }
        const p = cell.piece.toLowerCase();
        row += cell.color === 'w' ? p.toUpperCase() : p;
      } else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return ranks.join('/') + ' w - - 0 1';
}

function emojiToBg(emoji) {
  const safe = String(emoji).replace(/"/g, '');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text x='50' y='55' text-anchor='middle' dominant-baseline='central' font-size='68'>${safe}</text></svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

/**
 * Read-only mini board rendered inside each student card. Consumes ONLY the
 * `pieces` snapshot pushed via the existing /capture-progress channel — no
 * second sync, no writes back to the student. Falls back to the puzzle's
 * initial config until the student's first move arrives.
 */
function StudentLiveBoard({ pieces }) {
  const fen = useMemo(() => piecesToFen(pieces), [pieces]);
  const styles = useMemo(() => {
    const out = {};
    (pieces || []).forEach(p => {
      if (p.role === 'obstacle') {
        out[p.square] = {
          backgroundColor: 'rgba(100, 116, 139, 0.35)',
          backgroundImage: emojiToBg(p.emoji || '🧱'),
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: '78% 78%',
        };
      } else if (p.role === 'target') {
        out[p.square] = {
          backgroundColor: 'rgba(220, 38, 38, 0.30)',
          backgroundImage: emojiToBg(p.emoji || '🎯'),
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: '78% 78%',
        };
      }
    });
    return out;
  }, [pieces]);
  return (
    <div className="w-full aspect-square rounded-lg overflow-hidden pointer-events-none select-none" data-testid="student-live-board">
      <Chessboard options={{
        position: fen || EMPTY_FEN,
        boardOrientation: 'white',
        allowDragging: false,
        allowDrawingArrows: false,
        darkSquareStyle:  { backgroundColor: '#B58863' },
        lightSquareStyle: { backgroundColor: '#F0D9B5' },
        squareStyles: styles,
        showAnimations: true,
        animationDurationInMs: 150,
      }}/>
    </div>
  );
}

/**
 * Wide "Live View" style monitor rendered in the MAIN classroom area for the
 * teacher while a Capture Activity is running. Replaces the previous compact
 * sidebar card so the teacher's primary view during ASK is monitoring, not
 * a tiny chip in a 380px column.
 *
 * Responsibilities (per spec):
 *  · Big header: title + countdown + "Cerrar ejercicio" button.
 *  · Grid of large student cards showing name / status / captured / moves.
 *  · Real-time updates via the existing polling loop (room prop refreshes).
 *  · NEVER touches the teacher's FEN, arrows, circles or history: the server
 *    keeps the explanation board intact so Cerrar ejercicio simply unmounts
 *    this view and falls back to <ChessBoardView>.
 *  · Countdown uses ONLY p.duration_seconds + p.started_at (single source of
 *    truth; no secondary timer).
 */

const STATUS = {
  waiting:  { label: 'Esperando',   pill: 'bg-slate-400',   ring: 'border-slate-200 bg-white',      icon: Clock,           accent: 'text-slate-500' },
  solving:  { label: 'En progreso', pill: 'bg-amber-500',   ring: 'border-amber-300 bg-amber-50/60', icon: Loader2,         accent: 'text-amber-700' },
  solved:   { label: 'Resuelto',    pill: 'bg-emerald-500', ring: 'border-emerald-300 bg-emerald-50/60', icon: CheckCircle2, accent: 'text-emerald-700' },
};

export default function TeacherCaptureLiveView({ room, currentUserId }) {
  const p = room?.active_puzzle;
  const states = room?.puzzle_states || {};
  const participants = (room?.participants || []).filter(x => x.role !== 'teacher');

  // Countdown — same math the standard LivePuzzle uses, tied to the puzzle's
  // real started_at + duration_seconds. Ticks every 500ms for smooth updates.
  const [remaining, setRemaining] = useState(p?.duration_seconds || 0);
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (!p) return;
    const started = new Date(p.started_at).getTime();
    const dur = (p.duration_seconds || 60) * 1000;
    const tick = () => {
      const r = Math.max(0, Math.round((started + dur - Date.now()) / 1000));
      setRemaining(r);
      setExpired(r <= 0);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [p?.id, p?.started_at, p?.duration_seconds]);

  const totalTargets = useMemo(() => (
    (p?.capture_config?.pieces || []).filter(x => x.role === 'target').length
  ), [p?.capture_config]);

  const cards = useMemo(() => participants.map(pt => {
    const st = states[pt.user_id];
    const status = st?.status || 'waiting';
    // Prefer the student's most recent live snapshot. Before the first move
    // arrives (status='waiting'), fall back to the puzzle's initial pieces
    // so the teacher already sees the starting position of every student.
    const livePieces = Array.isArray(st?.capture_pieces) ? st.capture_pieces : null;
    const initialPieces = Array.isArray(p?.capture_config?.pieces) ? p.capture_config.pieces : [];
    return {
      userId: pt.user_id,
      name: pt.name,
      status,
      captured: st?.capture_captured ?? 0,
      total: st?.capture_total ?? totalTargets,
      moves: st?.capture_moves ?? 0,
      pieces: livePieces || initialPieces,
    };
  }), [participants, states, totalTargets, p?.capture_config]);

  const solvedCount = cards.filter(c => c.status === 'solved').length;
  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const cd = p?.duration_seconds || 0;
  const cdPct = cd > 0 ? Math.max(0, Math.min(100, (remaining / cd) * 100)) : 0;
  const cdBar = expired ? 'bg-rose-500' : remaining <= 10 ? 'bg-rose-500' : remaining <= 30 ? 'bg-amber-500' : 'bg-emerald-500';

  const endActivity = async () => {
    try { await api.post(`/classrooms/${room.id}/puzzle/end`); }
    catch { toast.error('No se pudo cerrar el ejercicio'); }
  };

  if (!p || !p.capture_config) return null;

  return (
    <div data-testid="teacher-capture-live-view" className="w-full h-full flex flex-col min-h-0">
      {/* Header — activity, countdown, Cerrar ejercicio */}
      <div className="rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50 via-white to-amber-50/40 p-5 mb-4 shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 rounded-2xl bg-amber-500 text-white grid place-items-center shrink-0 shadow-md">
              <Trophy className="w-6 h-6"/>
            </div>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Actividad de captura</div>
              <div className="text-2xl font-display font-extrabold text-slate-900 leading-tight truncate" data-testid="live-view-title">{p.title}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {solvedCount} de {cards.length} alumno{cards.length !== 1 ? 's' : ''} completó · {cards.length} conectado{cards.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
          <Button
            data-testid="end-capture-btn"
            size="lg"
            className="bg-rose-500 hover:bg-rose-600 text-white font-bold shrink-0 rounded-full px-6"
            onClick={endActivity}
          >
            <X className="w-5 h-5 mr-2"/> Cerrar ejercicio
          </Button>
        </div>

        {/* Countdown */}
        <div className="mt-4" data-testid="capture-countdown">
          <div className="flex items-center justify-between mb-2">
            <span className={`inline-flex items-center gap-2 font-mono ${expired ? 'text-rose-600 font-extrabold text-base' : 'text-slate-700 font-bold text-lg'}`}>
              <Timer className={`w-5 h-5 ${expired ? 'text-rose-500' : 'text-slate-500'}`}/>
              {expired ? 'Tiempo agotado' : `Tiempo restante: ${mm}:${ss}`}
            </span>
            <span className="text-xs font-mono text-slate-500">{cd}s totales</span>
          </div>
          <div className="h-2.5 bg-white rounded-full overflow-hidden border border-slate-200">
            <div className={`h-full rounded-full transition-all duration-500 ${cdBar}`} style={{width: `${cdPct}%`}}/>
          </div>
        </div>
      </div>

      {/* Student cards grid */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {cards.length === 0 ? (
          <div className="h-full grid place-items-center text-slate-400 text-sm border border-dashed border-slate-200 rounded-2xl">
            Sin alumnos conectados
          </div>
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {cards.map(card => {
              const s = STATUS[card.status] || STATUS.waiting;
              const Icon = s.icon;
              const spin = card.status === 'solving' ? 'animate-spin' : '';
              return (
                <div key={card.userId}
                  data-testid={`live-student-card-${card.userId}`}
                  className={`rounded-2xl border-2 ${s.ring} p-4 shadow-sm hover:shadow-md transition-shadow`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Alumno</div>
                      <div className="text-lg font-display font-bold text-slate-900 truncate" title={card.name}>{card.name}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full ${s.pill} text-white text-[11px] font-bold px-3 py-1 uppercase tracking-wider shrink-0`}>
                      <Icon className={`w-3.5 h-3.5 ${spin}`}/> {s.label}
                    </span>
                  </div>
                  {/* Read-only mini board — mirrors the student's live pieces */}
                  <div className="mb-3">
                    <StudentLiveBoard pieces={card.pieces}/>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-white border border-slate-200 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Objetos</div>
                      <div className={`text-2xl font-extrabold ${s.accent}`}>
                        {card.captured} <span className="text-slate-400 text-lg">/ {card.total}</span>
                      </div>
                    </div>
                    <div className="rounded-xl bg-white border border-slate-200 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Movimientos</div>
                      <div className={`text-2xl font-extrabold ${s.accent}`}>{card.moves}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
